/**
 * Announces which configuration surface the client is looking at.
 *
 * Legacy made each of these a step of its own, so the client's progress through
 * them was visible as a sequence of `enters_order_*_page_screen` events. v2 puts
 * them behind tabs of one step, which means nothing fires as the client moves —
 * the funnel stops at "reached configuration" and cannot say which forms were
 * reached. Firing on tab activation restores the legacy resolution.
 *
 * Keyed off the URL rather than the resolved tab index because the page
 * normalises its own route (a missing or unknown `:flowId` redirects to a real
 * one), so by the time both params are present they name a tab that exists.
 */

import { useEffect } from 'react'

import type { CartItemId, ConfigurationFlowId } from 'models/purchaseFlowRevamp'
import { PfRevampEventName } from '../../analytics/events'
import { MODULE_ANNOUNCED_CONFIG_FLOWS } from '../../analytics/legacyEvents'
import { dispatchPfRevampEvent } from '../../analytics/usePfRevampAnalytics'

export const useConfigScreenAnnouncement = (
  cartItemId: CartItemId | undefined,
  flowId: ConfigurationFlowId | undefined
): void => {
  useEffect(() => {
    if (!cartItemId || !flowId) return
    // A module that hosts several screens under one tab announces them itself —
    // the document configurator opens on property information, so announcing its
    // tab's own flow here would record a form the client has not reached.
    if (MODULE_ANNOUNCED_CONFIG_FLOWS.has(flowId)) return

    dispatchPfRevampEvent({
      type: PfRevampEventName.CONFIG_SCREEN_ENTERED,
      payload: { cartItemId, flowId },
    })
  }, [cartItemId, flowId])
}
